import { MainApi } from 'api/MainApi'
import { GAUSIUM_ENABLED } from 'utils/constants'

import { dedupeNormalizedApiRows } from './dedupeNormalizedApiRows'
import type { NormalizedStatisticsApiRow } from './filterableRecord'

const HISTORY_RANGE_MS = 90 * 24 * 60 * 60 * 1000

export function getDefaultHistoryRange(): { start: Date; end: Date } {
  const end = new Date()
  const start = new Date(end.getTime() - HISTORY_RANGE_MS)
  return { start, end }
}

/**
 * Autonomics + Gausium normalized rows (cache-first for GS), merged and deduped.
 * When `robotSn` is set, both sources are scoped to that robot.
 */
export async function fetchNormalizedHistoryMerged(params: {
  start: Date
  end: Date
  robotSn?: string
}): Promise<NormalizedStatisticsApiRow[]> {
  const { merged } = await fetchNormalizedHistoryMergedWithAutonomicsSnapshot(params)
  return merged
}

/** Same as fetchNormalizedHistoryMerged but keeps autonomics rows for GS refresh merge. */
export async function fetchNormalizedHistoryMergedWithAutonomicsSnapshot(params: {
  start: Date
  end: Date
  robotSn?: string
}): Promise<{
  merged: NormalizedStatisticsApiRow[]
  autonomicsRows: NormalizedStatisticsApiRow[]
}> {
  const base = {
    start_date: params.start.toISOString(),
    end_date: params.end.toISOString(),
    ...(params.robotSn ? { robot_sn: params.robotSn } : {}),
  }
  const [autoRes, gsCacheRes] = await Promise.all([
    MainApi.fetchUnifiedEvents({ ...base, source: 'autonomics' }),
    GAUSIUM_ENABLED
      ? MainApi.fetchUnifiedEvents({ ...base, source: 'gausium_task_reports', cache_only: true }).catch(
          () => ({ results: [] as NormalizedStatisticsApiRow[] })
        )
      : Promise.resolve({ results: [] as NormalizedStatisticsApiRow[] }),
  ])
  const autonomicsRows = (autoRes.results ?? []) as NormalizedStatisticsApiRow[]
  const merged = dedupeNormalizedApiRows([
    ...autonomicsRows,
    ...((gsCacheRes.results ?? []) as NormalizedStatisticsApiRow[]),
  ])
  return { merged, autonomicsRows }
}

/** Background refresh of GS rows from vendor-backed source (same merge semantics as TaskSchedulerNormalized). */
export async function refreshGausiumNormalizedHistory(params: {
  start: Date
  end: Date
  robotSn?: string
  autonomicsSnapshot: NormalizedStatisticsApiRow[]
}): Promise<NormalizedStatisticsApiRow[]> {
  if (!GAUSIUM_ENABLED) return params.autonomicsSnapshot
  const base = {
    start_date: params.start.toISOString(),
    end_date: params.end.toISOString(),
    ...(params.robotSn ? { robot_sn: params.robotSn } : {}),
  }
  const gsFull = await MainApi.fetchUnifiedEvents({ ...base, source: 'gausium_task_reports' }).catch(
    () => ({ results: [] as NormalizedStatisticsApiRow[] })
  )
  return dedupeNormalizedApiRows([
    ...params.autonomicsSnapshot,
    ...((gsFull.results ?? []) as NormalizedStatisticsApiRow[]),
  ])
}

/** One merged timeline per robot SN, then deduped (for facility multi-robot). */
export async function fetchNormalizedHistoryForRobotSerials(
  robotSns: string[],
  start: Date,
  end: Date
): Promise<NormalizedStatisticsApiRow[]> {
  const sns = Array.from(new Set(robotSns.map(s => String(s).trim()).filter(Boolean)))
  if (sns.length === 0) return []
  const chunks = await Promise.all(sns.map(sn => fetchNormalizedHistoryMerged({ start, end, robotSn: sn })))
  return dedupeNormalizedApiRows(chunks.flat())
}
